import { BodyText } from "@/components/ui";
import { copayAmountDisplay, copayMinMaxLabel } from "./CopayTable";
import type {
  FormularyCopay,
  PayerAlternative,
} from "./network/useFormularyLookup";

// Extract the preference level number from strings like "Preferred Level 2".
// Lower level = more preferred. Alternatives without a parseable level sort last.
export const payerAlternativePreferenceLevel = (alt: PayerAlternative) => {
  const text = alt.representativeFormularyCoverage?.formularyStatusText ?? "";
  const match = text.match(/(\d+)$/);
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
};

const getRepresentativeCopay = (
  alt: PayerAlternative,
): FormularyCopay | null => {
  const factors = alt.representativeFormularyCoverage?.copayFactors ?? [];
  // null pharmacyType means the copay applies to all pharmacy types (ANY in COP file),
  // so treat it as a retail match. Fall back to the first entry if no retail entry exists.
  return (
    factors.find(
      (c) =>
        c.pharmacyType == null || c.pharmacyType.toLowerCase() === "retail",
    ) ??
    factors[0] ??
    null
  );
};

const copayLabel = (copay: FormularyCopay): string => {
  const details = [
    copayAmountDisplay(copay),
    copayMinMaxLabel(copay),
    copay.daysSupply != null ? `${copay.daysSupply}d` : null,
    copay.copayTier != null
      ? `Tier ${copay.copayTier} ${copay.maximumCopayTier != null ? `(Max Tier ${copay.maximumCopayTier})` : ""}`
      : null,
  ].filter(Boolean);
  const detailStr = details.join(" ");
  if (copay.pharmacyType != null) {
    return detailStr
      ? `${copay.pharmacyType}: ${detailStr}`
      : copay.pharmacyType;
  }
  return detailStr;
};

export const AltBadge = ({
  value,
  color,
}: {
  value: string | null | undefined;
  color: "green" | "blue";
}) =>
  value ? (
    <span
      className={`rounded px-1.5 py-0.5 ${color === "green" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}
    >
      {value}
    </span>
  ) : (
    <span className="text-tertiary">—</span>
  );

const PayerAlternativeRow = ({ alt }: { alt: PayerAlternative }) => {
  const retailCopay = getRepresentativeCopay(alt);

  return (
    <div className="flex flex-col gap-0.5 text-sm py-1.5 px-3 border-b last:border-0">
      <span>{alt.displayName}</span>
      <div className="flex gap-2 text-xs">
        <AltBadge
          value={alt.representativeFormularyCoverage?.formularyStatusText}
          color="green"
        />
        <AltBadge
          value={retailCopay != null ? copayLabel(retailCopay) : null}
          color="blue"
        />
      </div>
    </div>
  );
};

const GROUPS = [
  { brand: true, otc: false },
  { brand: false, otc: false },
  { brand: true, otc: true },
  { brand: false, otc: true },
] as const;

interface PayerAlternativesProps {
  alternatives: PayerAlternative[];
}

export const PayerAlternatives = ({ alternatives }: PayerAlternativesProps) => {
  if (alternatives.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <BodyText size="sm" className="font-medium">
        Alternatives
      </BodyText>
      {GROUPS.map(({ brand, otc }) => {
        const group = alternatives.filter(
          (alt) => (alt.brand ?? false) === brand && (alt.otc ?? false) === otc,
        );
        if (group.length === 0) return null;
        return (
          <div key={`${brand}-${otc}`} className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                {brand ? "Brand" : "Generic"}
              </span>
              <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                {otc ? "OTC" : "Rx only"}
              </span>
            </div>
            <div className="rounded-md border overflow-hidden">
              {group.map((alt) => (
                <PayerAlternativeRow
                  key={alt.ndc ?? alt.displayName}
                  alt={alt}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
